import React from 'react';
import { Link } from 'react-router-dom';
import { ComponentSkinProps } from '../../../../types/component-skin';
import ReviewModal from '../../../common/ReviewModal';
import DeliveryModal from '../../../common/DeliveryModal';
import '../OrderHistory.css';

/**
 * PriceDisplay 컴포넌트
 * 가격을 포맷팅하여 표시
 */
const PriceDisplay: React.FC<{ price?: number }> = ({ price = 0 }) => {
  const formattedPrice = price?.toLocaleString() || '0';
  return <span>{formattedPrice}원</span>;
};

/**
 * 주문 내역 기본 스킨 - 원본 컴포넌트와 동일한 UI
 */
const BasicOrderHistorySkin: React.FC<ComponentSkinProps> = ({ 
  data, 
  actions, 
  options, 
  mode, 
  utils,
  app,
  editor
}) => {
  const { t } = utils;
  
  // 데이터 추출
  const { 
    orders,
    loading,
    error,
    currentPage,
    totalPages,
    searchQuery,
    selectedStatus,
    selectedDateRange,
    selectedOrder,
    showOrderDetail,
    isUserLoggedIn,
    isAdminMode,
    isMobile,
    pagination,
    checkouts, // 원본 호환성
    itemsPerPage = 10,
    title,
    emptyText,
    // 모달 관련
    reviewModalOpen,
    selectedProduct,
    selectedOrderForReview,
    deliveryModalOpen,
    selectedCheckout
  } = data;
  
  // 액션 추출
  const {
    setSearchQuery,
    setSelectedStatus,
    setSelectedDateRange,
    handleViewDetail,
    handleCancelOrder,
    handleTrackShipment,
    handleAddToCartAgain,
    handlePageChange,
    setShowOrderDetail,
    getStatusText,
    getStatusInfo,
    formatDate,
    // 모달 관련
    openReviewModal,
    handleReviewSubmitted,
    setReviewModalOpen,
    openDeliveryModal,
    setDeliveryModalOpen
  } = actions;
  
  // 옵션 추출 - 원본과 동일한 기본값
  const {
    id,
    style = {},
    className = "order-history-wrapper padding-tb-40"
  } = options;
  
  // 페이지네이션 컴포넌트 - 원본과 동일
  const Pagination = () => {
    // 페이지네이션 데이터 확인
    const totalPagesCalculated = pagination?.totalPages || Math.ceil((pagination?.totalItems || orders.length) / itemsPerPage) || 1;
    
    // 페이지 번호가 유효한지 확인
    const validCurrentPage = Math.min(Math.max(1, currentPage), totalPagesCalculated);

    // 표시할 페이지 번호 계산
    let pageNumbers = [];
    let startPage, endPage;

    if (isMobile) {
      // 모바일에서는 5개의 페이지 버튼만 표시 (현재 페이지 중심)
      const halfVisible = Math.floor(5 / 2);
      startPage = Math.max(1, validCurrentPage - halfVisible);
      endPage = Math.min(totalPagesCalculated, startPage + 4);

      // 만약 endPage가 totalPages보다 작으면 startPage 조정
      if (endPage < totalPagesCalculated && endPage - startPage < 4) {
        startPage = Math.max(1, endPage - 4);
      }
    } else {
      // 데스크톱/태블릿에서는 기존 방식 유지
      startPage = Math.max(1, validCurrentPage - 2);
      endPage = Math.min(totalPagesCalculated, startPage + 4);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    // 페이지 수가 1 이하면 페이지네이션 표시 안함
    if (totalPagesCalculated <= 1) return null;

    return (
      <div className="pagination-container">
        {!isMobile && (
          <>
            <button
              className="pagination-button prev"
              onClick={() => handlePageChange(1)}
              disabled={validCurrentPage === 1}
            >
              {t('처음')}
            </button>

            <button
              className="pagination-button prev"
              onClick={() => handlePageChange(validCurrentPage - 1)}
              disabled={validCurrentPage === 1}
            >
              {t('이전')}
            </button>
          </>
        )}

        {pageNumbers.map((number: number) => (
          <button
            key={number}
            className={`pagination-button ${number === validCurrentPage ? 'active' : ''}`}
            onClick={() => handlePageChange(number)}
          >
            {number}
          </button>
        ))}

        {!isMobile && (
          <>
            <button
              className="pagination-button next"
              onClick={() => handlePageChange(validCurrentPage + 1)}
              disabled={validCurrentPage === totalPagesCalculated}
            >
              {t('다음')}
            </button>

            <button
              className="pagination-button next"
              onClick={() => handlePageChange(totalPagesCalculated)}
              disabled={validCurrentPage === totalPagesCalculated}
            >
              {t('마지막')}
            </button>
          </>
        )}

        {isMobile && (
          <div className="pagination-info">
            {validCurrentPage} / {totalPagesCalculated} {t('페이지')}
          </div>
        )}
      </div>
    );
  };
  
  // 로딩 상태 표시
  if (loading) {
    return (
      <div className={className} id={id} style={style}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="order-history-loading">
                <p>{t('주문 내역을 불러오는 중입니다...')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 에러 표시
  if (error) {
    return (
      <div className={className} id={id} style={style}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="order-history-error">
                <p>{t('주문 내역을 불러오는데 실패했습니다')}: {typeof error === 'object' ? (error as any).message || JSON.stringify(error) : error}</p>
                <button
                  className="order-action-button primary-action"
                  onClick={() => window.location.reload()}
                >
                  {t('다시 시도')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 로그인 필요
  if (!isUserLoggedIn && !isAdminMode) {
    return (
      <div className={className} id={id} style={style}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="order-history-login-required">
                <p>{t('주문 내역을 확인하려면 로그인이 필요합니다.')}</p>
                <Link to="/login" className="order-action-button primary-action">{t('로그인')}</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 주문 내역이 비어있는 경우
  if (!orders || (Array.isArray(orders) && orders.length === 0)) {
    return (
      <div className={className} id={id} style={style}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="order-history-empty">
                <h2>{title}</h2>
                <p>{emptyText}</p>
                <Link to="/shop" className="order-action-button secondary-action">
                  {t('쇼핑 계속하기')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 주문 내역이 있는 경우 - 원본과 동일한 UI
  return (
    <div className={className} id={id} style={style}>
      <div className="container">
        <div className="row">
          <div className="col-12">
            <h2 className="order-history-title">{title}</h2>
            <p className="order-count">
              {t('총')} {pagination && (pagination.totalItems !== undefined ? pagination.totalItems : orders.length)}{t('개의 주문 내역이 있습니다.')}
              {!isMobile && pagination && pagination.totalPages > 1 &&
                ` (${pagination.currentPage}/${pagination.totalPages} ${t('페이지')})`
              }
            </p>
          </div>
        </div>

        <div className="order-history-list">
          {orders.map((order: any) => {
            const statusInfo = getStatusInfo(order.status);
            
            return (
              <div className="order-item" key={order.id}>
                {/* 모바일에서는 헤더 레이아웃 변경 */}
                {isMobile ? (
                  <div className="order-header mobile-header">
                    <div className="mobile-status-wrapper">
                      <span className={`order-status ${statusInfo.class}`}>
                        <span className="status-icon">{statusInfo.icon}</span> {statusInfo.text}
                      </span>
                      <div className="order-date mobile-date">{statusInfo.text === t('배송 완료') ? `${formatDate(order.createdAt)} ${t('도착')}` : formatDate(order.createdAt)}</div>
                    </div>
                  </div>
                ) : (
                  <div className="order-header">
                    <span className={`order-status ${statusInfo.class}`}>
                      <span className="status-icon">{statusInfo.icon}</span> {statusInfo.text}
                    </span>
                    <span className="order-date">{statusInfo.text === t('배송 완료') ? `${formatDate(order.createdAt)} ${t('도착')}` : formatDate(order.createdAt)}</span>
                  </div>
                )}
                <div className="order-content">
                  {/* 왼쪽: 상품 목록 */}
                  <div className="order-product">
                    {order.carts && order.carts.length > 0 ? (
                      <div className="order-products-list">
                        {order.carts.map((cartItem: any, index: number) => {
                          // 옵션 정보 파싱
                          const optionDisplay = cartItem.options?.options?.map((opt: any) =>
                            `${opt.groupName}: ${opt.valueName}`
                          ).join(', ');

                          return (
                            <div key={cartItem.id} className="order-product-item">
                              <div className="order-product-image">
                                {cartItem.product?.config?.img_url ? (
                                  <img
                                    src={cartItem.product?.config?.img_url}
                                    alt={cartItem.product?.title || t("상품")}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    onError={(e) => {
                                      e.currentTarget.src = 'https://via.placeholder.com/100?text=No+Image';
                                    }}
                                  />
                                ) : (
                                  <div style={{ width: '100%', height: '100%', background: '#f3f4f6' }}></div>
                                )}
                              </div>
                              <div className="order-product-info">
                                <div className="order-product-title" title={cartItem.product?.title || t('상품명 없음')}>
                                  {cartItem.product?.title || t('상품명 없음')}
                                </div>
                                {optionDisplay && (
                                  <div className="order-product-option">
                                    {optionDisplay}
                                  </div>
                                )}
                                <div className="order-product-price">
                                  <PriceDisplay
                                    price={parseInt(cartItem.unitPrice) || cartItem.product?.config?.discounted_price || cartItem.product?.config?.default_price}
                                  />
                                  <span className="order-product-count">× {cartItem.count}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}

                        {/* 주문 총액 표시 */}
                        <div className="order-total-section">
                          <div className="order-total-row">
                            <span className="order-total-label">{t('상품 금액')}</span>
                            <span className="order-total-value">
                              <PriceDisplay price={parseInt(order.parcel_price) || 0} />
                            </span>
                          </div>
                          {order.total_price !== order.parcel_price && (
                            <div className="order-total-row">
                              <span className="order-total-label">{t('배송비')}</span>
                              <span className="order-total-value">
                                <PriceDisplay price={parseInt(order.total_price) - parseInt(order.parcel_price)} />
                              </span>
                            </div>
                          )}
                          <div className="order-total-row order-total-final">
                            <span className="order-total-label">{t('총 결제금액')}</span>
                            <span className="order-total-value">
                              <PriceDisplay price={parseInt(order.total_price) || 0} />
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="empty-products">
                        {t('주문 상품 정보가 없습니다.')}
                      </div>
                    )}
                  </div>

                  {/* 오른쪽: 버튼 영역 */}
                  <div className="order-buttons">
                    <div className="order-actions">
                      {(order.status === "REQUESTED" || order.status === "PREPARING") && (
                        <button
                          className="order-action-button secondary-action"
                          onClick={() => handleCancelOrder(order.id)}
                        >
                          {t('주문 취소')}
                        </button>
                      )}
                      {(() => {
                        // parcels 배열에서 PREPARING, SHIPPING, ARRIVED 상태가 하나라도 있는지 확인
                        const hasValidParcelStatus = order.parcels && order.parcels.some((parcel: any) => 
                          parcel.status === "PREPARING" || parcel.status === "SHIPPING" || parcel.status === "ARRIVED"
                        );
                        
                        return hasValidParcelStatus && (
                          <button 
                            className="order-action-button primary-action"
                            onClick={() => openDeliveryModal(order)}
                          >
                            {t('배송조회')}
                          </button>
                        );
                      })()}
                      {order.status === "FINISHED" && (
                        <>
                          <button
                            className="order-action-button secondary-action"
                            onClick={() => alert(t('전체 주문에 대한 리뷰 작성 기능은 준비 중입니다.'))}
                          >
                            {t('리뷰 작성')}
                          </button>
                          <button
                            className="order-action-button primary-action"
                            onClick={() => handleAddToCartAgain(order)}
                          >
                            {t('같은 상품들 장바구니 담기')}
                          </button>
                        </>
                      )}
                      {(order.status === "CANCELLED" || order.status === "CANCELED") && (
                        <button
                          className="order-action-button primary-action"
                          onClick={() => handleAddToCartAgain(order)}
                        >
                          {t('같은 상품들 장바구니 담기')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* 페이지네이션 */}
          {pagination && pagination.totalPages > 0 && (
            <Pagination />
          )}
        </div>
      </div>

      {/* 리뷰 모달 */}
      <ReviewModal
        isOpen={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        productId={selectedProduct?.id}
        orderId={selectedOrderForReview}
        onReviewSubmitted={handleReviewSubmitted}
      />
      
      {/* 배송조회 모달 */}
      <DeliveryModal
        isOpen={deliveryModalOpen}
        onClose={() => setDeliveryModalOpen(false)}
        checkout={selectedCheckout}
      />
    </div>
  );
};

export default BasicOrderHistorySkin;